import { GridPaginationModel, GridSortItem } from '@mui/x-data-grid';

import { AgencyApprovalStatus, AgencyCategory } from '../../api/agency-api/models';

export interface AgencyListFilterState {
  search?: string;
  taxId?: string;
  displayName?: string;
  legalName?: string;
  county?: string;
  category?: AgencyCategory;
  bookId?: string;
  andarId?: string;
  isDonor?: boolean;
  isFederatedFund?: boolean;
  eftReceived?: boolean;
  hasMatchingGrant?: boolean;
  eventId?: string;
  approvalStatus?: AgencyApprovalStatus;
  isActive?: boolean;

  pagination: GridPaginationModel;
  orderBy: GridSortItem[];
}

interface Context {
  nextEventId?: string;
}

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT_FIELD = 'displayName';
const DEFAULT_SORT_DIRECTION: NonNullable<GridSortItem['sort']> = 'asc';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const SORT_FIELDS = ['displayName', 'legalName', 'taxId'];

// Plain text inputs/selects whose "cleared" value is the empty string.
const TEXT_KEYS = [
  'search',
  'taxId',
  'displayName',
  'legalName',
  'county',
  'bookId',
  'andarId',
] as const;

// Enum-backed selects, validated against their generated values so arbitrary URL input is rejected.
const ENUM_VALUES = {
  category: Object.values(AgencyCategory) as string[],
  approvalStatus: Object.values(AgencyApprovalStatus) as string[],
} as const;

type EnumKey = keyof typeof ENUM_VALUES;
const ENUM_KEYS = Object.keys(ENUM_VALUES) as EnumKey[];

// Tri-state selects that default to "None" — omitted when None, written as true/false otherwise.
const TRISTATE_KEYS = ['isDonor', 'isFederatedFund', 'eftReceived', 'hasMatchingGrant'] as const;

type TriState = '' | 'true' | 'false';

function normalizeTriState(value: unknown): TriState {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';
  return '';
}

/** The list's default state, with defaults living in exactly one place. */
export function defaultAgencyListState(nextEventId?: string): AgencyListFilterState {
  return {
    eventId: nextEventId,
    isActive: true,
    pagination: { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE },
    orderBy: [{ field: DEFAULT_SORT_FIELD, sort: DEFAULT_SORT_DIRECTION }],
  };
}

/**
 * Full default state for resetting the form. Cleared selects are set to the empty string so they
 * render their "None" option rather than an empty/uncontrolled value.
 */
export function resetAgencyListState(nextEventId?: string): AgencyListFilterState {
  const cleared = '' as unknown as undefined;
  return {
    ...defaultAgencyListState(nextEventId),
    search: '',
    taxId: '',
    displayName: '',
    legalName: '',
    county: '',
    category: cleared,
    bookId: '',
    andarId: '',
    isDonor: cleared,
    isFederatedFund: cleared,
    eftReceived: cleared,
    hasMatchingGrant: cleared,
    approvalStatus: cleared,
  };
}

/** Serialize the current filter state to a URLSearchParams, omitting values equal to the default. */
export function toSearchParams(
  state: AgencyListFilterState,
  { nextEventId }: Context
): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of TEXT_KEYS) {
    const value = state[key];
    if (value) params.set(key, String(value));
  }

  for (const key of ENUM_KEYS) {
    const value = state[key];
    if (value) params.set(key, String(value));
  }

  for (const key of TRISTATE_KEYS) {
    const value = normalizeTriState(state[key]);
    if (value) params.set(key, value);
  }

  // isActive defaults to "true"; an explicit "None" is written as the token `any`.
  const isActive = normalizeTriState(state.isActive);
  if (isActive === 'false') params.set('isActive', 'false');
  else if (isActive === '') params.set('isActive', 'any');

  // eventId defaults to the next event; a deliberate different event is written, an explicit clear as `none`.
  const { eventId } = state;
  if (eventId === '') params.set('eventId', 'none');
  else if (eventId && eventId !== nextEventId) params.set('eventId', eventId);

  const { page, pageSize } = state.pagination;
  if (page !== DEFAULT_PAGE) params.set('page', String(page));
  if (pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pageSize));

  const sort = state.orderBy[0];
  if (sort?.field && sort.field !== DEFAULT_SORT_FIELD) params.set('sort', sort.field);
  if (sort?.sort === 'desc') params.set('dir', 'desc');

  return params;
}

function parseTriState(params: URLSearchParams, key: string): TriState | undefined {
  const value = params.get(key);
  if (value === 'true') return 'true';
  if (value === 'false') return 'false';
  return undefined;
}

/** Parse a query string into a partial state; absent params are left out so callers can merge over defaults. */
export function fromSearchParams(
  params: URLSearchParams,
  { nextEventId }: Context
): Partial<AgencyListFilterState> {
  const state: Partial<AgencyListFilterState> = {};

  for (const key of TEXT_KEYS) {
    const value = params.get(key);
    if (value) (state as Record<string, unknown>)[key] = value;
  }

  // Only accept enum values that exist in the generated model; ignore anything else.
  for (const key of ENUM_KEYS) {
    const value = params.get(key);
    if (value && ENUM_VALUES[key].includes(value)) {
      (state as Record<string, unknown>)[key] = value;
    }
  }

  for (const key of TRISTATE_KEYS) {
    const value = parseTriState(params, key);
    if (value !== undefined) (state as Record<string, unknown>)[key] = value;
  }

  if (params.has('isActive')) {
    const value = params.get('isActive');
    if (value === 'false') (state as Record<string, unknown>).isActive = 'false';
    else if (value === 'any') (state as Record<string, unknown>).isActive = '';
    else if (value === 'true') (state as Record<string, unknown>).isActive = 'true';
  }

  if (params.has('eventId')) {
    const value = params.get('eventId');
    state.eventId = value === 'none' ? '' : value ?? nextEventId;
  }

  if (params.has('page') || params.has('pageSize')) {
    state.pagination = {
      page: parsePage(params.get('page')),
      pageSize: parsePageSize(params.get('pageSize')),
    };
  }

  if (params.has('sort') || params.has('dir')) {
    state.orderBy = [
      { field: parseSortField(params.get('sort')), sort: parseSortDirection(params.get('dir')) },
    ];
  }

  return state;
}

function parsePage(value: string | null): number {
  const page = Number(value);
  return Number.isInteger(page) && page >= 0 ? page : DEFAULT_PAGE;
}

function parsePageSize(value: string | null): number {
  const pageSize = Number(value);
  return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE;
}

function parseSortField(value: string | null): string {
  return value && SORT_FIELDS.includes(value) ? value : DEFAULT_SORT_FIELD;
}

function parseSortDirection(value: string | null): NonNullable<GridSortItem['sort']> {
  return value === 'desc' ? 'desc' : DEFAULT_SORT_DIRECTION;
}
